import { makeStyles } from '@material-ui/styles'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import {
  maxValueValidator,
  minValueValidator,
  rangeValueValidator,
  vnidRangeValidator,
} from 'core/utils/fieldValidators'
import { routeManager } from 'core/utils/routes'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import { useCallback, useEffect, useState } from 'react'
import useReactRouter from 'use-react-router'
import { trackEvent } from 'utils/tracking'
import { getBlueprints } from '../infrastructure/actions'
import { createNetwork, createSubnet, listNetworks } from './actions'
import AdminStatePicklist from './AdminStatePicklist'
import {
  DNS_ZONE_INFO,
  MAKE_SHARED_INFO,
  NetworkMode,
  SIMPLE_NETWORK_TOOLTIP_INFO,
  MANAGED_NETWORK_INFO,
} from './constants'
import ZonePicklist from './dns/records/ZonePicklist'
import {
  getPhysicalNetworkRequestBody,
  getSimpleNetworkRequestBody,
  addSimpleNetworkTag,
} from './helpers'
import { getIPv6AdditionalFields } from './IPv6AddressConfigurationModePicklist'
import PhysicalNetworkLabelPicklist from './PhysicalNetworkLabelPicklist'
import PhysicalNetworkTypePicklist from './PhysicalNetworkTypePicklist'
import { AdvancedSubnetFields, BasicSubnetFields } from './SubnetFormFields'
import SelectableCard from 'core/components/SelectableCard'
import Text from 'core/elements/Text'
import Alert from 'core/components/Alert'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import Radio from 'core/elements/input/Radio'
import Tooltip from 'core/elements/tooltip'

const useStyles = makeStyles<Theme>((theme) => ({
  networkModeCards: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  cardWrapper: {
    position: 'relative',

    width: '100%',
  },
  card: {
    width: '100%',
    height: '100%',
  },
  cardBody: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: '16px',
    alignItems: 'center',
  },
  betaBadge: {
    position: 'absolute',
    top: 15,
    right: -10,
    backgroundColor: theme.palette.blue['700'],
    color: theme.palette.common.white,
    padding: theme.spacing(1, 2, 1, 3),
    whiteSpace: 'nowrap',
    zIndex: 10,
    display: 'inline-block',
    clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 10px 50%)',
  },
  infoIcon: {
    color: theme.palette.grey[500],
    fontSize: '20px',
    cursor: 'help',
  },
  content: {
    justifySelf: 'left',
  },

  tootipAlignment: {
    alignSelf: 'end',
  },
  tooltipBody: {
    maxWidth: '200px',
  },
}))

interface Props {
  addRoute: Route
}

export default function CreatePhysicalNetworkModal({ addRoute }: Props) {
  const classes = useStyles({})
  const { history } = useReactRouter()
  const [createdNetworkId, setCreatedNetworkId] = useState(null)
  const [virtualNetworkSetting, setVirtualNetworkSetting] = useState(null)
  const [networkMode, setNetworkMode] = useState<NetworkMode | null>(null)
  const defaultParams = {
    networkMode: null, // Progressive disclosure: user must select type first
    networkType: '',
    externalNetworkType: 'flat',
    segmentationId: '',
    name: '',
    description: '',
    shared: false,
    adminState: 'Up',
    createSubnet: true,
    ipVersion: 4,
    cidr: '',
    gatewayIp: '',
    disableGateway: false,
    allocationPools: [],
    dnsNameServers: [],
    hostRoutes: [],
    enableDhcp: true,
    ipv6AddressConfigurationMode: '',
    external: false,
    networkLabel: '',
    enablePortSecurity: true,
    mtu: 1500,
    dns_domain: '',
    dns_publish_fixed_ip: false,
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const { reload } = useListAction(listNetworks, {})
  const { update, updating, error, reset } = useUpdateAction(createNetwork)
  const {
    update: updateSubnet,
    updating: updatingSubnet,
    error: subnetError,
    reset: resetSubnet,
  } = useUpdateAction(createSubnet)

  const createNetworkFn = async (params) => {
    // Use appropriate request body builder based on network mode
    const body =
      networkMode === NetworkMode.SIMPLE
        ? getSimpleNetworkRequestBody(params)
        : getPhysicalNetworkRequestBody(params)

    const { success, response } = await update({ body })

    // For Simple Networks, add tag after creation
    if (success && networkMode === NetworkMode.SIMPLE && response?.id) {
      try {
        await addSimpleNetworkTag(response.id)
      } catch (error) {
        console.error('Failed to add simple-network tag:', error)
        // Don't fail the entire operation if tagging fails
        // Network is created, tag can be added manually if needed
      }
    }

    return { success, response }
  }

  const createSubnetFn = async (params, networkId) => {
    const allocationPools = params.allocationPools.map((entry) => {
      const [start, end] = entry.replace(/\s/g, '').split('-')
      return {
        start,
        end,
      }
    })
    const hostRoutes = params.hostRoutes.map((entry) => {
      const [cidr, nexthop] = entry.replace(/\s/g, '').split(',')
      return {
        destination: cidr,
        nexthop,
      }
    })
    const body = {
      subnet: {
        name: params.subnetName,
        network_id: networkId,
        ip_version: params.ipVersion,
        cidr: params.cidr,
        gateway_ip: params.disableGateway ? null : params.gatewayIp ? params.gatewayIp : undefined,
        allocation_pools: allocationPools.length ? allocationPools : undefined,
        dns_nameservers: params.dnsNameServers.length ? params.dnsNameServers : undefined,
        host_routes: hostRoutes.length ? hostRoutes : undefined,
        enable_dhcp: params.enableDhcp,
        dns_publish_fixed_ip: params.dns_publish_fixed_ip,
        ...getIPv6AdditionalFields(params),
      },
    }
    const { success } = await updateSubnet({ body })
    return { success }
  }

  const submitForm = useCallback(async () => {
    const shouldCreateSubnet = networkMode === NetworkMode.MANAGED && params.createSubnet

    // Handle retry case: network already created, just create subnet
    if (createdNetworkId) {
      if (shouldCreateSubnet) {
        const { success } = await createSubnetFn(params, createdNetworkId)
        if (success) {
          reload(true, true)
          handleClose()
        }
      } else {
        handleClose()
      }
      return
    }

    // Create network
    const { success, response } = await createNetworkFn(params)
    if (!success) {
      return
    }

    trackEvent(`Create Network - Finished`)

    // Create subnet if needed
    if (shouldCreateSubnet) {
      const networkId = response?.id
      setCreatedNetworkId(networkId)
      const { success: subnetSuccess } = await createSubnetFn(params, networkId)
      if (!subnetSuccess) {
        return
      }
      trackEvent(`Create Subnet - Finished`)
    }

    // Success: reload and close
    reload(true, true)
    handleClose()
  }, [params, networkMode, createdNetworkId])

  const fetchBlueprints = async () => {
    try {
      const blueprints = (await getBlueprints()) as any[]
      const blueprintSettings = blueprints?.[0]
      setVirtualNetworkSetting(blueprintSettings?.virtualNetworking)
    } catch (error) {
      console.error('Failed to fetch blueprints:', error)
    }
  }

  useEffect(() => {
    fetchBlueprints()
  }, [])

  const handleClose = () => {
    setParams(defaultParams)
    setNetworkMode(null) // Reset to no selection (progressive disclosure)
    reset()
    resetSubnet()
    setCreatedNetworkId(null)
    history.push(routeManager.routes.openstack.networks.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create Physical Network`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || updatingSubnet}
      error={error || subnetError}
      submitTitle={`Create Physical Network`}
      disableSubmit={networkMode === null}
      disableSubmitMessage={
        networkMode === null ? 'Please select a network type to continue' : undefined
      }
    >
      <>
        <ServiceUnhealthyInfo action="Network Creation" network />

        {/* Step 1: Basic Information (always visible) */}
        <FormFieldSection title="Basic Information">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            required
          />
          <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
          />
          <AdminStatePicklist value={params.adminState} onChange={getParamsUpdater('adminState')} />
          <TextField
            id="mtu"
            label="MTU"
            onChange={(value) => getParamsUpdater('mtu')(value as number)}
            validations={[minValueValidator(1280), maxValueValidator(9000)]}
            value={params.mtu}
            type="number"
            info="Maximum Transmission Unit (MTU) for the network. MTU must be between 1280 and 9000"
          />
        </FormFieldSection>

        {/* Step 2: Network Mode Selection */}
        <FormFieldSection title="Select Network Type">
          <div className={classes.networkModeCards}>
            <div className={classes.cardWrapper}>
              <SelectableCard
                id="managed"
                onClick={() => {
                  setNetworkMode(NetworkMode.MANAGED)
                  updateParams({
                    networkMode: NetworkMode.MANAGED,
                    createSubnet: true,
                    enablePortSecurity: true,
                  })
                }}
                active={networkMode === NetworkMode.MANAGED}
                className={classes.card}
                withCustomBody
              >
                <div className={classes.cardBody}>
                  <Radio checked={networkMode === NetworkMode.MANAGED} />
                  <Text variant="caption1" className={classes.content}>
                    Managed Network
                  </Text>
                  <Tooltip
                    customClassName={classes.tooltipBody}
                    className={classes.tootipAlignment}
                    message={MANAGED_NETWORK_INFO}
                  >
                    <HelpOutlineIcon className={classes.infoIcon} />
                  </Tooltip>
                </div>
              </SelectableCard>
            </div>
            <div className={classes.cardWrapper}>
              <Text variant="caption1" className={classes.betaBadge}>
                Beta
              </Text>
              <SelectableCard
                id="simple"
                onClick={() => {
                  setNetworkMode(NetworkMode.SIMPLE)
                  updateParams({
                    networkMode: NetworkMode.SIMPLE,
                    networkType: 'flat', // Default to L2-only for Simple Networks
                    createSubnet: false,
                    external: false,
                    enablePortSecurity: false,
                  })
                }}
                active={networkMode === NetworkMode.SIMPLE}
                className={classes.card}
                withCustomBody
              >
                <div className={classes.cardBody}>
                  <Radio checked={networkMode === NetworkMode.SIMPLE} />
                  <Text variant="caption1" className={classes.content}>
                    Layer 2 Network
                  </Text>
                  <Tooltip
                    customClassName={classes.tooltipBody}
                    className={classes.tootipAlignment}
                    message={SIMPLE_NETWORK_TOOLTIP_INFO}
                  >
                    <HelpOutlineIcon className={classes.infoIcon} />
                  </Tooltip>
                </div>
              </SelectableCard>
            </div>
          </div>
        </FormFieldSection>

        {/* Step 3: Network Configuration (shown only after network type selected) */}
        {networkMode !== null && (
          <FormFieldSection title="Network Configuration">
            {/* Network Label - Required for both Managed and Simple Networks */}
            <PicklistField
              DropdownComponent={PhysicalNetworkLabelPicklist}
              id="networkLabel"
              label="Network Label"
              value={params.networkLabel}
              onChange={getParamsUpdater('networkLabel')}
              info="Select the network label that maps this network to the corresponding physical network on your hypervisors"
              required
            />

            {/* MANAGED ONLY: External Network checkbox */}
            {networkMode === NetworkMode.MANAGED && (
              <CheckboxField
                id="external"
                label="Allow use of Public IPs (External Network)"
                value={params.external}
                onChange={getParamsUpdater('external')}
                info="Set checked if the network should be able to assign public IPs (external network)"
              />
            )}

            {/* Network Type - Same for both Managed and Simple Networks */}
            <PicklistField
              DropdownComponent={PhysicalNetworkTypePicklist}
              id="networkType"
              label="Network Type"
              value={params.networkType}
              onChange={getParamsUpdater('networkType')}
              required
            />

            {/* VLAN ID - shown for both modes when network type is vlan */}
            {params.networkType === 'vlan' && (
              <TextField
                id="segmentationId"
                label={'VLAN ID'}
                onChange={getParamsUpdater('segmentationId')}
                value={params.segmentationId}
                validations={[
                  rangeValueValidator(1, 4094),
                  vnidRangeValidator({
                    vnidRange: virtualNetworkSetting?.vnidRange,
                    underlayType: params.networkType,
                  }),
                ]}
                required
                type="number"
                min="0"
              />
            )}

            {/* MANAGED ONLY: DNS Zone */}
            {networkMode === NetworkMode.MANAGED && (
              <PicklistField
                DropdownComponent={ZonePicklist}
                id="zoneId"
                label="DNS Zone"
                onChange={getParamsUpdater('dns_domain')}
                value={params.dns_domain}
                info={DNS_ZONE_INFO}
                setLabelAsValue={true}
              />
            )}

            {/* MANAGED ONLY: Port Security */}
            {networkMode === NetworkMode.MANAGED && (
              <CheckboxField
                id="enablePortSecurity"
                label="Enable Port Security"
                value={params.enablePortSecurity}
                onChange={getParamsUpdater('enablePortSecurity')}
                info="If checked, newly created ports on this network will have port security enabled by default."
              />
            )}

            {/* Make Shared - Available for both Managed and Simple Networks */}
            <CheckboxField
              id="shared"
              label="Make Shared (Across All Tenants)"
              value={params.shared}
              onChange={getParamsUpdater('shared')}
              info={MAKE_SHARED_INFO}
            />
            {/* MANAGED ONLY: Create Subnet checkbox */}
            {networkMode === NetworkMode.MANAGED && (
              <CheckboxField
                id="createSubnet"
                label="Create Subnet"
                value={params.createSubnet}
                onChange={getParamsUpdater('createSubnet')}
                info="Create a subnet for this network now."
                tooltipAlign={{
                  vertical: 'middle',
                  horizontal: 'left',
                }}
              />
            )}
          </FormFieldSection>
        )}

        {/* SIMPLE NETWORK: Info Banner */}
        {/* {networkMode === NetworkMode.SIMPLE && (
          <Alert variant="primary" message={SIMPLE_NETWORK_INFO} />
        )} */}

        {/* MANAGED ONLY: Subnet Fields */}
        {networkMode === NetworkMode.MANAGED && params.createSubnet && (
          <>
            <BasicSubnetFields
              params={params}
              getParamsUpdater={getParamsUpdater}
              inNetworkCreation
            />
            <AdvancedSubnetFields params={params} updateParams={updateParams} />
          </>
        )}
      </>
    </ModalForm>
  )
}
